import {
  Composite,
  CompositeGroup,
  CompositeGroupLabel,
  CompositeGroupLabelProps,
  CompositeGroupProps,
  CompositeItem,
  CompositeItemProps,
  CompositeProps,
  CompositeProvider,
  CompositeRow,
  CompositeStore,
} from "@ariakit/react";
import { CompositeStoreItem } from "@ariakit/react-core/composite/composite-store";
import { createElement } from "@ariakit/react-core/utils/system";
import { Options, Props } from "@ariakit/react-core/utils/types";
import { rgba } from "polished";
import React, {
  type JSX,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import styled, { css } from "styled-components";

import { composeRefs } from "../hooks/useCompositeRef";
import { useId } from "../hooks/useUniqueId";

import { HexButton } from "./HexButton";
import { HexDivider, HexDividerProps } from "./HexDivider";
import { HexPopover, HexPopoverProps } from "./HexPopover";
import { HexTooltip, HexTooltipProps } from "./HexTooltip";
import { Txt } from "./Txt";
import { itemFocusStyles, stopPropagation } from "./utils";

// --- Root ---

export type HexGridListRootProps<T extends React.ElementType = "div"> =
  CompositeProps<T> & {
    // --- Reiterations of CompositeProviderProps ---
    // Omits...
    // - `orientation` since grid list should always be two-dimensional
    // - `id` since SSR is not a concern

    /**
     * Determines how the focus behaves when the user reaches the end of
     * the grid list.
     *
     * - `true` loops from the last row/column item to the first item in
     * the same row/column and vice-versa. If it's the last item in the
     * last row, it moves to the first item in the first row and vice-versa.
     * - `horizontal` loops only from the last row item to the first item
     * in the same row.
     * - `vertical` loops only from the last column item to the first item
     * in the column row.
     *
     * - If `includesBaseElement` is set to true (or `activeId` is initially
     * set to `null`), vertical loop will have no effect as moving down
     * from the last row or up from the first row will focus on the composite
     * element.
     * - If `focusWrap` matches the value of `focusLoop`, it'll wrap between
     * the last item in the last row or column and the first item in the first
     * row or column and vice-versa.
     *
     * Recommendations:
     *
     * - Allow looping in the direction of list orientation when the list is the
     * main element of the section.
     * - Allow looping when there are many list items so to provide alternative
     * navigation to the end of the list for keyboard users without `End` key.
     * - Disable looping in direction of list orientation when there are multiple
     * lists in sequence, to prevent unintuitive navigation back to start of
     * current list instead of to start of next list.
     */
    focusLoop?: boolean | "horizontal" | "vertical";
    /**
     * If enabled, moving up or down when there's no next item or when the next
     * item is disabled will shift to the item right before it.
     *
     * Recommendations:
     *
     * - Allow shift when there are conditionally rendered widgets in list items
     * so that focus does not go out of bounds when navigating
     */
    focusShift?: boolean;
    /**
     * If enabled, moving to the next item from the last one in a row or column
     * will focus on the first item in the next row or column and vice-versa.
     *
     * - `true` wraps between rows and columns.
     * - `horizontal` wraps only between rows.
     * - `vertical` wraps only between columns.
     *
     * If `focusLoop` matches the value of `focusWrap`, it'll wrap between the
     * last item in the last row or column and the first item in the first row
     * or column and vice-versa.
     */
    focusWrap?: boolean | "horizontal" | "vertical";
    /**
     * Determines how the next and previous functions will behave. If `rtl` is set
     * to true, they will be inverted.
     *
     * This only affects the behavior. You still need to set `dir="rtl"` on HTML/CSS.
     */
    rtl?: boolean;
    /**
     * Indicates if the grid list base element (Root) should be part of the
     * focus order when navigating with arrow keys. In other words, moving to
     * the previous element when the first item is in focus will focus on the grid
     * list element itself. The same applies to the last item when moving to the
     * next element.
     */
    includesBaseElement?: boolean;
    /**
     * If enabled, the composite element will act as an aria-activedescendant
     * container instead of roving tabindex. DOM focus will remain on the
     * composite element while its items receive virtual focus.
     *
     * In both scenarios, the item in focus will carry the data-active-item
     * attribute.
     */
    virtualFocus?: boolean;
    /**
     * Another store object that will be kept in sync with the original store.
     */
    store?: CompositeStore;
    /**
     * Lists all grid list items and item widgets along with their metadata.
     * This state is automatically updated when an item or item widget is
     * registered or unregistered using the registerItem function.
     */
    items?: CompositeStoreItem[];
    /**
     * The default value for the `items` state.
     */
    defaultItems?: CompositeStoreItem[];
    /**
     * A callback fired when the `items` state changes.
     */
    onItemsChange?: (items: CompositeStoreItem[]) => void;
    /**
     * The current active grid list item or item widget id. The active id is the
     * element within the grid list that has either DOM or virtual focus (in
     * case virtualFocus is enabled).
     *
     * `null` represents the base element (Root). Users will be able to navigate
     * out of it using arrow keys. If `activeId` is initially set to `null`, the
     * `includesBaseElement` prop will also default to `true`, which means the
     * element itself will have focus and users will be able to navigate to it
     * using arrow keys.
     */
    activeId?: string | null;
    /**
     * The grid list item id that should be active by default when the grid list
     * is rendered. If `null`, the grid list element itself will have focus and
     * users will be able to navigate to it using arrow keys. If `undefined`,
     * the first enabled item will be focused.
     */
    defaultActiveId?: string | null;
    /**
     * A callback fired when the `activeId` state changes.
     */
    onActiveIdChange?: (activeId: string | null | undefined) => void;
  };

type HexGridListRootComponent = (<T extends React.ElementType = "div">(
  props: Props<T, HexGridListRootProps<T>>,
) => JSX.Element) &
  string;

/**
 * A root element of the grid list.
 */
const Root_ = React.memo(
  (props: HexGridListRootProps & { ref?: React.Ref<HTMLDivElement> }) => {
    const {
      activeId,
      defaultActiveId,
      defaultItems,
      focusLoop,
      focusShift,
      focusWrap,
      includesBaseElement,
      items,
      onActiveIdChange,
      onItemsChange,
      ref,
      ...rest
    } = props;

    const [labelId, setLabelId] = React.useState<string>();

    return (
      <CompositeProvider
        activeId={activeId}
        defaultActiveId={defaultActiveId}
        defaultItems={defaultItems}
        focusLoop={focusLoop}
        focusShift={focusShift}
        focusWrap={focusWrap}
        includesBaseElement={includesBaseElement}
        items={items}
        setActiveId={onActiveIdChange}
        setItems={onItemsChange}
      >
        <Composite ref={ref} aria-labelledby={labelId} role="grid" {...rest}>
          <LabelContext value={setLabelId}>{props.children}</LabelContext>
        </Composite>
      </CompositeProvider>
    );
  },
);

const Root: HexGridListRootComponent = styled(Root_)``;

// --- Label ---

export type HexGridListLabelProps<T extends React.ElementType = "div"> = Props<
  T,
  Options
>;

type HexGridListLabelComponent = (<T extends React.ElementType = "div">(
  props: Props<T, HexGridListLabelProps<T>>,
) => JSX.Element) &
  string;

const LabelContext = React.createContext<
  ((value: string | undefined) => void) | undefined
>(undefined);

const Label_ = React.memo(
  ({
    ref,
    ...props
  }: HexGridListLabelProps & { ref?: React.Ref<HTMLDivElement> }) => {
    const setLabelId = React.useContext(LabelContext);
    const id = useId(props.id);

    useLayoutEffect(() => {
      setLabelId?.(id);
      return () => setLabelId?.(undefined);
    }, [setLabelId, id]);

    const htmlProps: React.HTMLAttributes<HTMLDivElement> = {
      id,
      ...props,
    };

    return createElement("div", { ...htmlProps, ref });
  },
);

const Label: HexGridListLabelComponent = styled(Label_)``;

// --- Group ---

export type HexGridListGroupProps<T extends React.ElementType = "div"> =
  CompositeGroupProps<T>;

type HexGridListGroupComponent = (<T extends React.ElementType = "div">(
  props: Props<T, HexGridListGroupProps<T>>,
) => JSX.Element) &
  string;

/**
 * Renders a group element for related grid list rows or items.
 */
const Group_ = React.memo(
  ({
    ref,
    ...props
  }: HexGridListGroupProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return <CompositeGroup ref={ref} role="rowgroup" {...props} />;
  },
);

const Group: HexGridListGroupComponent = styled(Group_)``;

// --- GroupLabel ---

export type HexGridListGroupLabelProps<T extends React.ElementType = "div"> =
  CompositeGroupLabelProps<T>;

type HexGridListGroupLabelComponent = (<T extends React.ElementType = "div">(
  props: Props<T, HexGridListGroupLabelProps<T>>,
) => JSX.Element) &
  string;

/**
 * Renders a label in a grid list group. This component must be wrapped in `HexGridList.Group`.
 */
const GroupLabel_ = React.memo(
  ({
    ref,
    ...props
  }: HexGridListGroupLabelProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return <CompositeGroupLabel ref={ref} aria-hidden={false} {...props} />;
  },
);

const GroupLabel: HexGridListGroupLabelComponent = styled(GroupLabel_)``;

// --- Divider ---

export interface HexGridListDividerProps extends HexDividerProps {}

type HexGridListDividerComponent = ((
  props: HexGridListDividerProps,
) => JSX.Element) &
  string;

/**
 * Renders a divider for separating groups, rows, or items in a grid list.
 */
// no `forwardRef` because BP doesn't really support them
const Divider_ = function HexGridListDivider(
  props: HexGridListDividerProps,
): JSX.Element {
  return <HexDivider $muted={true} {...props} />;
};

const Divider: HexGridListDividerComponent = styled(Divider_)``;

// --- Item ---

export type HexGridListItemProps<T extends React.ElementType = "div"> =
  CompositeItemProps<T> & {
    selected?: boolean;
  };

type HexGridListItemComponent = (<T extends React.ElementType = "div">(
  props: Props<T, HexGridListItemProps<T>>,
) => JSX.Element) &
  string;

/**
 * Renders a row element as a focusable item for grid list items that allows two-dimensional arrow key navigation.
 */
const Item_ = React.memo(
  (props: HexGridListItemProps & { ref?: React.Ref<HTMLDivElement> }) => {
    const { children, id: idProp, ref, selected, ...rest } = props;

    const id = useId(idProp);
    const [labelId, setLabelId] = React.useState<string>();

    return (
      <CompositeRow
        ref={ref}
        aria-labelledby={labelId}
        aria-selected={selected}
        id={id}
        render={
          <CompositeItem
            render={<div />}
            // Since we're using div as a render prop and have set the default type
            // above to div, then we know `rest` is safe, but the `CompositeItem`
            // component type isn't generic and doesn't know that
            // But we do want to pass the props to `CompositeItem`, instead of `div
            // so that consumers have access to `CompositeItemProps`
            {...(rest as HexGridListItemProps<"button">)}
          />
        }
        role="row"
      >
        <ItemLabelContext value={setLabelId}>
          <div aria-colindex={1} css="display: contents;" role="gridcell">
            {children}
          </div>
        </ItemLabelContext>
      </CompositeRow>
    );
  },
);

const Item: HexGridListItemComponent = styled(Item_)`
  ${itemFocusStyles}

  /* not [data-focus-visible="true"] because that's also applied when
  the user clicks on the item, and then navigates to an inner widget */
  &[data-focus-visible]:not(:focus-visible)::after {
    box-shadow: none;
  }

  &:hover {
    cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
    background-color: ${({ theme }) => theme.hoverColor};
  }

  &[aria-selected="true"] {
    background-color: ${({ theme }) => theme.menuItem.activeBackground};
    color: ${({ theme }) => theme.menuItem.activeText};
    &:hover {
      background-color: ${({ theme }) =>
        rgba(theme.menuItem.activeBackground, 0.1)};
    }
    &:active {
      background-color: ${({ theme }) =>
        rgba(theme.menuItem.activeBackground, 0.13)};
    }
  }
  &[aria-disabled="true"] {
    ${({ theme }) => theme.disabledState};
  }
`;

// --- itemChildStyles ---

export type HexGridListItemChildStylesProps = {
  /**
   * Whether to show the component by default.
   * @default true
   */
  visible?: boolean;
  /**
   * Whether to show the component when the item is hovered or focused, or when an
   * interactive descendant is hovered or focused.
   * @default false
   */
  visibleOnItemInteraction?: boolean;
  /**
   * Whether to show the component when the item is selected.
   * @default false
   */
  visibleOnItemSelection?: boolean;
};

/**
 * A custom `styled` that encapsulates useful styling props for children of
 * `HexGridList.Item`.
 *
 * @example
 * ```tsx
 * const MyComponentItemChild = HexGridList.styledItemChild(MyComponent)`
 *   /* any other styles *\/
 * `;
 *
 * <HexGridList.Item>
 *   <MyComponentItemChild visible={false} visibleOnItemInteraction={true} />
 * </HexGridList.Item>
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/explicit-function-return-type -- matching React's types
const styledItemChild = <P = {},>(component: React.ComponentType<P>) =>
  styled(
    styled(component).withConfig({
      shouldForwardProp: (prop) =>
        typeof prop === "string" &&
        ![
          "visible",
          "visibleOnItemInteraction",
          "visibleOnItemSelection",
        ].includes(prop),
    })<HexGridListItemChildStylesProps>`
      && {
        ${({
          visible = true,
          visibleOnItemInteraction = false,
          visibleOnItemSelection = false,
        }) => [
          visible
            ? css`
                opacity: 1;
                position: relative;
              `
            : css`
                opacity: 0;
                position: absolute;
              `,
          visibleOnItemInteraction &&
            css`
              ${Item}:hover &,
            ${Item}[data-focus-visible="true"] &,
            ${Item}:has([data-focus-visible="true"]) & {
                opacity: 1;
                position: relative;
              }
            `,
          visibleOnItemSelection &&
            css`
              ${Item}[aria-selected="true"] & {
                opacity: 1;
                position: relative;
              }
            `,
        ]}
      }
    ` as React.ComponentType<P & HexGridListItemChildStylesProps>,
  );

// --- ItemLabel ---

export type HexGridListItemLabelProps<T extends React.ElementType = "div"> =
  Props<T, Options>;

type HexGridListItemLabelComponent = (<T extends React.ElementType = "div">(
  props: Props<T, HexGridListItemLabelProps<T>>,
) => JSX.Element) &
  string;

const ItemLabelContext = React.createContext<
  ((value: string | undefined) => void) | undefined
>(undefined);

/**
 * Renders an accessible label associated with a grid list item.
 *
 * IMPORTANT: There should be exactly **one** `ItemLabel` per `HexGridList.Item`.
 * Do not use both `ItemLabel` and a derivative of it in the same `HexGridList.Item`.
 * Do not use `ItemLabel` for text that is not the label of the item.
 */
const ItemLabel_ = React.memo(
  ({
    ref,
    ...props
  }: HexGridListItemLabelProps & { ref?: React.Ref<HTMLDivElement> }) => {
    const htmlProps = useItemLabel(props);

    return createElement("div", { ...htmlProps, ref });
  },
);

const ItemLabel: HexGridListItemLabelComponent = styled(ItemLabel_)``;

const useItemLabel = <T extends HexGridListItemLabelProps>(props: T): T => {
  const setLabelId = React.useContext(ItemLabelContext);
  const id = useId(props.id);

  useLayoutEffect(() => {
    setLabelId?.(id);
    return () => setLabelId?.(undefined);
  }, [setLabelId, id]);

  return {
    id,
    ...props,
  };
};

// --- ItemWidget ---

export type HexGridListItemWidgetProps<T extends React.ElementType = "span"> =
  CompositeItemProps<T> & {
    /** Whether to show the widget. @default true */
    visible?: boolean;
    /** Whether to show the widget when the item is hovered or focused. @default false */
    visibleOnItemInteraction?: boolean;
    /** Whether to show the widget when the item is selected. @default false */
    visibleOnItemSelection?: boolean;
  };

type HexGridListItemWidgetComponent = (<T extends React.ElementType = "span">(
  props: Props<T, HexGridListItemWidgetProps<T>>,
) => JSX.Element) &
  string;

const ItemWidget_ = React.memo(
  ({
    ref,
    ...props
  }: HexGridListItemWidgetProps & { ref?: React.Ref<HTMLSpanElement> }) => {
    return (
      <CompositeItem
        // since the component isn't generically typed, we can't clue it in that
        // we're rendering a span. we don't want to apply it to the span in the
        // render prop directly, because then a consumer wouldn't be able to pass
        // a ref through from the Widget to their custom render prop, which is
        // sometimes necessary for certain compositions.
        // @ts-expect-error -- ref mismatch HTMLButtonElement -> HTMLSpanElement
        ref={ref}
        render={<ItemWidgetElement />}
        // Since we're using div as a render prop and have set the default type
        // above to div, then we know `rest` is safe, but the `CompositeItem`
        // component type isn't generic and doesn't know that
        // But we do want to pass the props to `CompositeItem`, instead of `div
        // so that consumers have access to `CompositeItemProps`
        {...(props as HexGridListItemProps<"button">)}
      />
    );
  },
);

const ItemWidgetElement = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- withConfig is fine
const ItemWidget: HexGridListItemWidgetComponent = styledItemChild(ItemWidget_)`
  ${itemFocusStyles}
`;

// --- ItemButton ---

export type HexGridListItemButtonProps = React.ComponentProps<
  typeof HexButton
> &
  Pick<
    HexGridListItemWidgetProps<"button">,
    | "visible"
    | "visibleOnItemInteraction"
    | "visibleOnItemSelection"
    | "accessibleWhenDisabled"
    | "disabled"
  >;

type HexGridListItemButtonComponent = ((
  props: Props<"button", HexGridListItemButtonProps>,
) => JSX.Element) &
  string;

const ItemButton_ = React.memo(
  ({
    accessibleWhenDisabled,
    disabled,
    onClick,
    ref,
    visible,
    visibleOnItemInteraction,
    visibleOnItemSelection,
    ...rest
  }: HexGridListItemButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
    const handleClick = useCallback<React.MouseEventHandler<HTMLElement>>(
      (event) => {
        event.stopPropagation();
        onClick?.(event);
      },
      [onClick],
    );

    return (
      <ItemWidget
        accessibleWhenDisabled={accessibleWhenDisabled}
        disabled={disabled}
        render={<HexButton onClick={handleClick} {...rest} ref={ref} />}
        visible={visible}
        visibleOnItemInteraction={visibleOnItemInteraction}
        visibleOnItemSelection={visibleOnItemSelection}
      />
    );
  },
);

const ItemButton: HexGridListItemButtonComponent = styled(ItemButton_)`
  &&&&&&&[aria-expanded="true"] {
    background-color: ${({ theme }) => theme.activeColor};
  }
`;

// --- ItemButtonPopover ---

export type HexGridListItemButtonPopoverProps = Omit<
  HexPopoverProps,
  "renderTarget" | "children" | "content"
> & {
  /**
   * Render the content of the popover.
   */
  renderContent?:
    | React.ReactNode
    /**
     * A function to render the content of the popover.
     *
     * @param api An imperative API for the popover
     * @param api.closePopover A callback to close the popover. For example, on activation of a menu item.
     * @returns Exactly one `ReactNode`
     */
    | ((api: { closePopover: () => void }) => React.ReactNode);
  /**
   * Render the target of the popover.
   *
   * @param targetProps A set of HTML props that must be passed to the target element
   * @param popoverState The current state of the popover
   * @returns Exactly one `ReactNode`
   */
  children: (
    targetProps: Omit<
      NonNullable<HexPopoverProps["renderTarget"]>,
      "tabIndex" | "isOpen"
    >,
    popoverState: { isOpen: boolean },
  ) => React.ReactNode;
};

/**
 * Display floating content next to a button within a grid list item.
 *
 * Use Popover instead of Tooltip if content is sufficiently complex and/or
 * displayed on click/activation (spacebar, enter, etc.) rather than just on
 * hover/focus. For example, a menu of actions or a surface with some additional
 * action(s).
 *
 * @example
 * <HexGridList.ItemButtonPopover renderContent={...}>
 *   {(props) => (
 *     <HexGridList.ItemButton {...props} />
 *   )}
 * </HexGridList.ItemButtonPopover>
 *
 * // content with api
 * <HexGridList.ItemButtonPopover renderContent={(api) => ...}>
 *   {(props) => (
 *     <HexGridList.ItemButton {...props} />
 *   )}
 * </HexGridList.ItemButtonPopover>
 *
 * // when content does not contain interactive elements, focus should remain on the button
 * <HexGridList.ItemButtonPopover
 *   renderContent={...}
 *   autoFocus={false}
 *   enforceFocus={false}
 *   shouldReturnFocusOnClose={false}
 * >
 *   {(props) => (
 *     <HexGridList.ItemButton {...props} />
 *   )}
 * </HexGridList.ItemButtonPopover>
 */
const ItemButtonPopover = React.memo(function HexGridListItemButtonPopover({
  children,
  renderContent,
  ...rest
}: HexGridListItemButtonPopoverProps) {
  const [open, setOpen] = useState(false);

  const handleInteraction = useCallback<
    NonNullable<HexPopoverProps["onInteraction"]>
  >((nextOpen) => {
    setOpen(nextOpen);
  }, []);

  const closePopover = useCallback(() => {
    setOpen(false);
  }, []);

  const renderTarget = useCallback<
    NonNullable<HexPopoverProps["renderTarget"]>
  >(
    // omit `tabIndex` so that ItemWidget can control it
    ({ isOpen, tabIndex: _, ...targetProps }) => {
      const popoverState = { isOpen };
      return <>{children(targetProps, popoverState)}</>;
    },
    [children],
  );

  const disabled = renderContent == null;

  const api = useMemo(() => ({ closePopover }), [closePopover]);

  const content = useMemo(() => {
    if (typeof renderContent === "function") {
      return renderContent(api);
    }
    return renderContent;
  }, [renderContent, api]);

  return (
    <HexPopover
      content={
        <div css="display: contents;" onClick={stopPropagation}>
          {content}
        </div>
      }
      disabled={disabled}
      isOpen={open}
      renderTarget={renderTarget}
      onInteraction={handleInteraction}
      {...rest}
    />
  );
});

// --- ItemButtonTooltip ---

export type HexGridListItemButtonTooltipProps = Omit<
  HexTooltipProps,
  "renderTarget" | "children"
> & {
  /**
   * Render the target of the tooltip.
   *
   * @param targetProps A set of HTML props that must be passed to the target element.
   * @param tooltipState The current state of the tooltip.
   * @returns Exactly one `ReactNode`
   */
  children: (
    targetProps: Omit<
      NonNullable<HexTooltipProps["renderTarget"]>,
      "tabIndex" | "isOpen"
    >,
    tooltipState: { isOpen: boolean },
  ) => React.ReactNode;
};

/**
 * Display floating content next to a button within a grid list item.
 *
 * Use Tooltip rather than Popover if content is simple, doesn't contain
 * interactive elements, and is displayed on hover/focus rather than on
 * activation (click, spacebar, enter, etc.). For example, a label for an
 * icon button.
 *
 * @example
 * <HexGridList.ItemButtonTooltip content="My tooltip" placement="bottom">
 *   {(props) => (
 *     <HexGridList.ItemButton {...props} />
 *   )}
 * </HexGridList.ItemButtonTooltip>
 */
const ItemButtonTooltip = React.memo(function HexGridListItemButtonTooltip({
  children,
  content,
  ...rest
}: HexGridListItemButtonTooltipProps) {
  const renderTarget = useCallback<
    NonNullable<HexTooltipProps["renderTarget"]>
  >(
    // omit `tabIndex` so that ItemWidget can control it
    ({ isOpen, tabIndex: _, ...targetProps }) => {
      const tooltipState = { isOpen };
      return <>{children(targetProps, tooltipState)}</>;
    },
    [children],
  );

  return <HexTooltip content={content} {...rest} renderTarget={renderTarget} />;
});

// --- ItemLabelTooltip ---

export type HexGridListItemLabelTooltipProps = Omit<
  HexTooltipProps,
  "renderTarget" | "children"
> & {
  children?: React.ReactElement<any>;
};

/**
 * Display floating content next to a label within a grid list item.
 *
 * @example
 * <HexGridList.ItemLabelTooltip content="My tooltip" placement="bottom">
 *   <HexGridList.ItemLabel>My label</HexGridList.ItemLabel>
 * </HexGridList.ItemLabelTooltip>
 */
const ItemLabelTooltip = React.memo(
  ({
    children,
    content,
    disabled: disabledProp,
    ref,
    ...rest
  }: HexGridListItemLabelTooltipProps & {
    ref?: React.Ref<HTMLSpanElement>;
  }) => {
    const disabled = disabledProp ?? content == null;

    const renderTarget = useCallback<
      NonNullable<HexTooltipProps["renderTarget"]>
    >(
      ({ isOpen: _isOpen, ref: targetRef, tabIndex, ...targetProps }) => {
        return (
          <ItemWidget
            ref={composeRefs(ref, targetRef)}
            disabled={disabled}
            render={children}
            {...targetProps}
          />
        );
      },
      [children, disabled, ref],
    );

    return (
      <HexTooltip
        {...rest}
        content={content}
        disabled={disabled}
        renderTarget={renderTarget}
      />
    );
  },
);

// --- ItemLabelTxt ---

export type HexGridListItemLabelTxtProps = React.ComponentProps<typeof Txt>;

type HexGridListItemLabelTxtComponent = ((
  props: Props<"span", HexGridListItemLabelTxtProps>,
) => JSX.Element) &
  string;

/**
 * A wrapper for the `Txt` component that is used to render the label of a grid list item.
 *
 * IMPORTANT: There should be exactly **one** `ItemLabel` per `HexGridList.Item`.
 * Do not use both `ItemLabel` and `ItemLabelTxt` in the same `HexGridList.Item`.
 * Do not use `ItemLabelTxt` for text that is not the label of the item (use `Txt`)
 */
export const ItemLabelTxt_ = React.memo(
  ({
    ref,
    ...props
  }: HexGridListItemLabelTxtProps & { ref?: React.Ref<HTMLSpanElement> }) => {
    return <ItemLabel render={<Txt {...props} ref={ref} />} />;
  },
);

const ItemLabelTxt: HexGridListItemLabelTxtComponent = styled(ItemLabelTxt_)``;

// --- Module ---

/**
 * A grid list displays a list of interactive items with focusable elements within
 * rows and keyboard navigation.
 *
 * Compared to a list box, a grid list can also support a one-dimensional list of
 * selectable items. Beyond this, a grid list can support a list of selectable items
 * that have other actions associated with them (focusable elements within rows). A
 * grid list can also support navigation that is one or two dimensional where list
 * items are not focusable, but have focusable elements within them.
 *
 * @example
 * <caption>two-dimensional list with focusable items and focusable elements within items</caption>
 * ```jsx
 * <HexGridList.Root>
 *   <HexGridList.Item>
 *     Item 1
 *     <HexGridList.ItemWidget>Item 1.1</HexGridList.ItemWidget>
 *     <HexGridList.ItemWidget>Item 1.2</HexGridList.ItemWidget>
 *   </HexGridList.Item>
 *   <HexGridList.Item>
 *     Item 2
 *     <HexGridList.ItemWidget>Item 2.1</HexGridList.ItemWidget>
 *     <HexGridList.ItemWidget>Item 2.2</HexGridList.ItemWidget>
 *   </HexGridList.Item>
 * </HexGridList.Root>
 * ```
 *
 * @example
 * <caption>two-dimensional list with grouped items</caption>
 * ```jsx
 * <HexGridList.Root>
 *   <HexGridList.Group>
 *     <HexGridList.GroupLabel>Group 1</HexGridList.GroupLabel>
 *     <HexGridList.Item />
 *     <HexGridList.Item />
 *   </HexGridList.Group>
 *   <HexGridList.Group>
 *     <HexGridList.GroupLabel>Group 2</HexGridList.GroupLabel>
 *     <HexGridList.Item />
 *     <HexGridList.Item />
 *   </HexGridList.Group>
 * </HexGridList.Root>
 * ```
 */
export const HexGridList = Object.freeze({
  Root: Root,
  Label: Label,
  Group: Group,
  GroupLabel: GroupLabel,
  Divider: Divider,
  Item: Item,
  ItemLabel: ItemLabel,
  ItemLabelTxt: ItemLabelTxt,
  ItemLabelTooltip: ItemLabelTooltip,
  ItemWidget: ItemWidget,
  ItemButton: ItemButton,
  ItemButtonPopover: ItemButtonPopover,
  ItemButtonTooltip: ItemButtonTooltip,
  styledItemChild: styledItemChild,
});
